import { motion, useReducedMotion } from "framer-motion";
import Link from "next/link";
import { useLayoutEffect, useState, type MouseEvent, type Ref } from "react";

import CartChip from "@/blueprint/components/NavPill/CartChip";
import NavMenuGlyph from "@/blueprint/components/NavPill/NavMenuGlyph";
import { NAV_SHOP_CTA_CLASSES } from "@/blueprint/components/NavPill/navShopCta";
import {
  MotionDuration,
  MotionTransition,
  NavMotion,
  seconds,
} from "@/blueprint/tokens/Motion";
import { cn } from "@/lib/utils";

const NAV_EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

/** The cart chip's slot: 4px of Figma's Buttons gap plus the 32px chip. */
const CART_SLOT_PX = 36;

/** Shop navigates on the resting nav; inside the sheet it opens the sub-view. */
export type MobileNavShopAction =
  { kind: "link"; href: string } | { kind: "button"; onClick: () => void };

/**
 * `row` is the in-flow nav cluster, `pill` the floating scrolled one. They
 * differ only in the halo and the cart chip's padding. Whichever one opens
 * the menu stays put as the open sheet's header — there is no second copy.
 */
export type MobileNavClusterVariant = "row" | "pill";

type MobileNavClusterProps = {
  variant: MobileNavClusterVariant;
  shopLabel: string;
  shopAction: MobileNavShopAction;
  shopButtonRef?: Ref<HTMLButtonElement> | undefined;
  cartCount: number;
  onCartClick: () => void;
  onMenuClick: (event: MouseEvent<HTMLButtonElement>) => void;
  menuOpen: boolean;
  menuButtonRef?: Ref<HTMLButtonElement> | undefined;
  /** id of the disclosure region the menu button controls. */
  menuControlsId?: string | undefined;
  /**
   * The shop sub-view keeps only the close control (Figma State=Shop renders
   * the Buttons group at opacity 0), holding its width so the X never moves.
   */
  shopHidden?: boolean | undefined;
  className?: string | undefined;
};

const MobileNavCluster = ({
  variant,
  shopLabel,
  shopAction,
  shopButtonRef,
  cartCount,
  onCartClick,
  onMenuClick,
  menuOpen,
  menuButtonRef,
  menuControlsId,
  shopHidden = false,
  className,
}: MobileNavClusterProps) => {
  const reducedMotion = useReducedMotion() === true;
  const [cartVisualPresent, setCartVisualPresent] = useState(cartCount > 0);
  useLayoutEffect(() => {
    if (cartCount > 0) setCartVisualPresent(true);
  }, [cartCount]);
  const shopClasses = cn(
    NAV_SHOP_CTA_CLASSES,
    "relative block px-[18px] py-xs",
  );
  // Once the cart has items the chip takes Shop's place in the closed nav —
  // the row is tight — but the open menu always carries Shop (Figma Open:
  // Buttons = Shop + Cart), so it reveals itself as part of the open morph.
  const shopVisible = cartCount === 0 || menuOpen;
  // A dismissal beats its entry — the decision is already made.
  const morph = {
    duration: reducedMotion ? 0 : menuOpen ? 0.2 : 0.15,
    ease: [...MotionTransition.navMorph.ease] as [
      number,
      number,
      number,
      number,
    ],
  };

  return (
    <div
      /* The cluster is the anchor the sheet grows out of, so it holds still:
         anything it did here would be the anchor moving under the surface. It
         used to slide 20px inward to sit in the open card's padding box, back
         when the card was a separate element that appeared around it. */
      className={cn("relative flex items-center gap-xs", className)}
      data-nav-cluster={variant}
    >
      {/* The scrolled pill's backdrop, haloing 4px around the controls (Figma
          Mobile Nav Scrolled; always solid white, never translucent). It
          belongs to the scrolled state only — Figma's Open state has no halo,
          and its overshoot would poke past the open card's edge — so it fades
          out with the open morph. */}
      {variant === "pill" && (
        <motion.div
          initial={false}
          animate={{ opacity: menuOpen ? 0 : 1 }}
          /* Cut, not faded, and only on the way open. The sheet surface starts
             the morph on this exact rect, so the two are the same pixels on
             that frame and the swap is invisible; leaving it to fade would
             float a bordered pill over the growing sheet instead. Coming back
             it waits for the surface to finish collapsing onto it. */
          transition={
            menuOpen
              ? { duration: 0 }
              : {
                  duration: 0,
                  delay: reducedMotion
                    ? 0
                    : seconds(
                        NavMotion.surfaceClose.durationMs +
                          NavMotion.contentOut.durationMs,
                      ),
                }
          }
          className="absolute -inset-1 rounded-full border border-tint-black-20 bg-brand-white"
        />
      )}
      {/* Figma's Buttons group; the 4px Shop↔cart gap rides inside the chip's
          slot. Shop fades in place rather than sliding: its slot snaps to
          size before the fade-in and collapses only after the fade-out, and
          because the cluster is right-anchored the snap moves nothing else. */}
      <motion.div
        initial={false}
        animate={{ opacity: shopHidden ? 0 : 1 }}
        aria-hidden={shopHidden}
        inert={shopHidden}
        transition={{
          duration: reducedMotion ? 0 : MotionDuration.quickMs / 1000,
          ease: NAV_EASE_OUT,
        }}
        className="relative flex items-center"
      >
        <motion.div
          initial={false}
          animate={{
            width: shopVisible ? "auto" : 0,
            opacity: shopVisible ? 1 : 0,
          }}
          transition={{
            opacity: morph,
            width: {
              duration: 0,
              delay: shopVisible || reducedMotion ? 0 : morph.duration,
            },
          }}
          aria-hidden={!shopVisible}
          inert={!shopVisible}
          className={cn(
            "shrink-0 overflow-hidden",
            !shopVisible && "pointer-events-none",
          )}
        >
          {shopAction.kind === "link" ? (
            <Link
              href={shopAction.href}
              data-nav-control="shop"
              className={cn(shopClasses, "w-max")}
            >
              {shopLabel}
            </Link>
          ) : (
            <button
              ref={shopButtonRef}
              type="button"
              data-nav-control="shop"
              onClick={shopAction.onClick}
              className={cn(shopClasses, "w-max")}
            >
              {shopLabel}
            </button>
          )}
        </motion.div>
        <motion.div
          initial={false}
          animate={{ width: cartCount > 0 ? CART_SLOT_PX : 0 }}
          transition={{
            duration: reducedMotion ? 0 : MotionDuration.quickMs / 1000,
            ease: NAV_EASE_OUT,
          }}
          onAnimationComplete={() => {
            if (cartCount === 0) setCartVisualPresent(false);
          }}
          className="shrink-0 overflow-hidden"
        >
          {/* Unmounts once the collapse animation ends: an empty cart must
              not remain an invisible tab stop. */}
          {cartVisualPresent && (
            <CartChip
              count={cartCount}
              onClick={onCartClick}
              navControl="cart"
              className="ml-1 size-xl"
            />
          )}
        </motion.div>
      </motion.div>
      <button
        ref={menuButtonRef}
        type="button"
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-expanded={menuOpen}
        aria-controls={menuControlsId}
        data-nav-control="menu"
        onClick={onMenuClick}
        className="relative flex size-xl items-center justify-center text-content-primary"
      >
        <NavMenuGlyph open={menuOpen} reducedMotion={reducedMotion} />
      </button>
    </div>
  );
};

export default MobileNavCluster;
